// =====================================================================================
// FILE: src/airtable.js (FIXED - View-based filtering + fetchAllAssets)
// 
// ✅ KEY FIX: When viewName is provided, use ONLY the Airtable view for filtering
// (don't also apply filterByFormula since view names don't match fixture field values)
// ✅ NEW: Added fetchAllAssets() to populate asset cache for empty rows
// =====================================================================================

// Operator base config — needed for delete cleanup (operator tables may be in a different base)
import { getOperatorBase } from './config/operatorBases';

const TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;

// Out of Game table
const TABLE_ID = process.env.REACT_APP_AIRTABLE_TABLE_ID;

// OPTIONAL: In Game table
const IN_GAME_TABLE_ID = process.env.REACT_APP_AIRTABLE_INGAME_TABLE_ID || "";

// OPTIONAL: IPTV table
const IPTV_TABLE_ID = process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID || "";

// OPTIONAL: LED Entitlements table
const LED_TABLE_ID = process.env.REACT_APP_AIRTABLE_LEDENTITLEMENTS_TABLE_ID || "";

// OPTIONAL: Team List table
const TEAMLIST_TABLE_ID = process.env.REACT_APP_AIRTABLE_TEAMLIST_TABLE_ID || "";

// Assets table
const ASSETS_TABLE_ID = process.env.REACT_APP_AIRTABLE_ASSETS_TABLE_ID;

// Fixtures table
const FIXTURES_TABLE_ID = process.env.REACT_APP_AIRTABLE_FIXTURES_TABLE_ID;

// ✅ NEW: View Config table
const VIEWCONFIG_TABLE_ID = process.env.REACT_APP_AIRTABLE_VIEWCONFIG_TABLE_ID || "";

// ✅ Operator View tables (for cleanup on delete)
const CG_OPERATOR_TABLE_ID = process.env.REACT_APP_AIRTABLE_CG_OPERATOR_TABLE_ID || "";
const RIBBON_OPERATOR_TABLE_ID = process.env.REACT_APP_AIRTABLE_RIBBON_OPERATOR_TABLE_ID || "";

// ✅ FIX: required whenever we request cellFormat=string
const AIRTABLE_STRING_FORMAT_TZ = "Australia/Melbourne";
const AIRTABLE_STRING_FORMAT_LOCALE = "en-AU";

// ================= FIELDS =================
export const FIELDS = {
  fixture: "Fixtures",
  cue: "Cue ID",
  category: "Category",

  name: "Name",
  playerNumber: "Player Number",
  clientSponsor: "Client or Sponsor Name",
  notes: "Lumen8 Comments",

  contentStatus: "Content Status",
  clubInstructions: "Club Instructions",
  hasContent: "Has Content",
  
  // ✅ Team List venue field (determines which view the row appears in)
  destinationVenue: "Destination Venue",

  // ✅ Audit fields (Airtable "Last Modified Time" and "Last Modified By" field types)
  lastModifiedTime: "Last Modified",
  lastModifiedBy: "Last Modified By",
  
  // ✅ VenueFlow editor tracking (custom field we control - stores actual Supabase user)
  venueFlowEditor: "VenueFlow Editor",

  // LED-only
  allocationId: "Allocation ID",
  breaks: "Breaks", // Multi-select: Q1, Q2, Q3, Q4

  // IPTV-only
  iptvGfx: "IPTV GFX",
  rotations: "Rotations",
  bonusPriority: "Bonus Priority",								  
  selectTiming: "Select Timing",
  selectLocation: "Select Location",

  // Production asset links
  bigGfxVideos: "Big Screen - GFX & Videos",
  cornerGfxVideos: "Corner Screen - GFX & Videos",
  ledAllTypes: "LED - All Types",
  bigTvc: "Big Screen - TVC",
  cornerTvc: "Corner Screen - TVC", // ✅ NEW: Marvel TVC content

  superFull: "SuperScreen – Fullscreen / Takeovers",
  super169: "SuperScreen - 20:9 Safe Graphics",
  superSide: "SuperScreen - Side Panels",

  mcgInterchange: "MCG Interchange",
  marvelInterchange: "Marvel Interchange",

  marvel360: "Marvel 360 LED",
  mcg360: "MCG 360 LED",

  mcgTiledLed: "MCG Tiled LED",
  marvelTiledLed: "Marvel Tiled LED",
};

// ================= VIEW CONFIG FIELD NAMES =================
const VIEWCONFIG_FIELDS = {
  viewName: "View Name",
  fixtureKey: "Fixture Key",
  activeOnly: "Active Only",
};

// ================= IPTV FIELD ALIASES =================
const IPTV_FIELD_ALIASES = {
  iptvGfx: [FIELDS.iptvGfx, "IPTV GFX ", "IPTV Gfx", "IPTV Graphics", "IPTV GFX Asset"],
  rotations: [FIELDS.rotations, "Rotation", "Rotation Count", "Rotations "],
  bonusPriority: [FIELDS.bonusPriority, "Bonus Priority ", "Bonus Order", "Priority"],																					  
  selectTiming: [
    FIELDS.selectTiming,
    "Select Timing ",
    "Select Timing (New Column - Multi Select)",
    "Select Timing (Multi Select)",
    "Timing",
  ],
  selectLocation: [
    FIELDS.selectLocation,
    "Select Location ",
    "Select Location (New Column - Multi Select)",
    "Select Location (Multi Select)",
    "Location",
  ],
};

// ✅ IPTV option whitelists
const IPTV_TIMING_OPTIONS = [
  "Pre-Match",
  "During ALL Match",
  "During 1st Quarter Time",
  "During Half Time",
  "During Three Quarter Time",
  "Post-Match",
  "Post Match",
  "Special Gates",
];

const IPTV_LOCATION_OPTIONS = [
  "Harrison Room",
  "Ryder Room",
  "Hassett Room",
  "Miller Room",
  "Yarra Park Room",
  "Gate 1",
  "Gate 4",
  "Gate 5",
  "Gate 6",
  "Gate 7",
  "Victory Room",
  "Victory C-E Screens",
  "Quill Room",
  "Quill Room Wall LED",
  "Parker Room",
  "Rogers Room",
  "Skyline Room",
  "Horizon Room",
  "Danvers Room",
  "Stark",
];

const IPTV_TIMING_SET = new Set(IPTV_TIMING_OPTIONS);
const IPTV_LOCATION_SET = new Set(IPTV_LOCATION_OPTIONS);

function isIptvTableId(tableId) {
  return Boolean(IPTV_TABLE_ID && tableId === IPTV_TABLE_ID);
}

function isLedTableId(tableId) {
  return Boolean(LED_TABLE_ID && tableId === LED_TABLE_ID);
}

function isTeamListTableId(tableId) {
  return Boolean(TEAMLIST_TABLE_ID && tableId === TEAMLIST_TABLE_ID);
}

function isInGameTableId(tableId) {
  return Boolean(IN_GAME_TABLE_ID && tableId === IN_GAME_TABLE_ID);
}

// ================= STATUS VALUES =================
// MatchFlow Builder (Club View): New Submission, Rejected, Approved by Lumen8
// Operator View: Ready for Testing, Ready for Playout, Rejected, Approved by Lumen8
export const STATUS = {
  // MatchFlow Builder statuses
  NEW_SUBMISSION: "New Submission",
  REJECTED: "Rejected",
  APPROVED_BY_LUMEN8: "Approved by Lumen8",
  // Operator View statuses
  READY_FOR_TESTING: "Ready for Testing",
  READY_FOR_PLAYOUT: "Ready for Playout",
};

// ================= ASSET FIELD HELPERS =================
const ASSET_FIELDS = {
  // ✅ Reordered: Filename and Name first to get full asset names for better searchability
  titleCandidates: ["File/Folder Name", "Filename", "Name", "Title", "Asset ID"],
  thumbCandidates: ["Asset Thumbnails", "Thumb", "Thumbnail", "Image", "Artwork", "Preview"],
  destinationCandidates: ["Asset Destination", "Destination", "Asset Destination (Text)"],
  // ✅ NEW: Metadata fields for tooltips
  hasAlphaCandidates: ["Has Alpha", "Has Alpha?", "Alpha", "Alpha?", "Transparency", "Has Transparency"],
  hasAudioCandidates: ["Has Audio", "Has Audio?", "Audio", "Audio?", "Sound", "Has Sound"],
  // ✅ NEW: Preview URL for video playback (rich text with markdown links)
  previewUrlCandidates: ["Asset Preview URL", "Preview URL", "Preview", "Video URL", "Playback URL"],
  // ✅ NEW: Asset Spec Summary (text field from Airtable)
  specSummaryCandidates: ["Asset Spec Summary", "Spec Summary", "Specs"],
  // ✅ NEW: Asset Download URL (raw URL)
  downloadUrlCandidates: ["Asset Download URL (raw)", "Asset Download URL", "Download URL", "Download"],
  // ✅ NEW: Asset Type (video/image)
  assetTypeCandidates: ["Asset Type", "Type", "File Type", "Media Type"],
  // ✅ NEW: Submitter Email for contact functionality
  submitterEmailCandidates: ["Submitter Email", "Uploader Email", "User Email", "Email", "Submitted By Email", "Contact Email"],
  // ✅ NEW: Lumen8 ID for operator linking
  lumenIdCandidates: ["Lumen8 ID", "Lumen ID", "Asset ID", "ID"],
  // ✅ NEW: Archived checkbox for hiding old assets
  archivedCandidates: ["Archived", "Is Archived", "Hidden", "Is Hidden"],
  // ✅ NEW: Round Allocations JSON for TVC round tagging
  roundAllocationsCandidates: ["Round Allocations", "Round Allocation", "Rounds Allocation"],
  // ✅ NEW: Uploader Type for filtering TVC content
  uploaderTypeCandidates: ["Uploader Type", "Upload Type", "Source Type"],
  // ✅ NEW: Content metadata for commercial playlist enrichment
  tierCandidates: ["Tier", "Partner Tier", "Client Tier"],
  categoryCandidates: ["Category", "Partner Category", "Client Category"],
  primaryColourCandidates: ["Primary Colour", "Colour", "Primary Color", "Color", "Partner Colour"],
  // ✅ NEW: Asset FilePath for content delivery (Send Assets → Make.com)
  filePathCandidates: ["Asset FilePath", "FilePath", "File Path"],
  // ✅ NEW: Venue field — MCG/Marvel/SCG etc from Master Content Database.
  // Surfaced on TVC playlist rows as a small tag so the team can see at a
  // glance which venue a club's uploaded asset was targeted for, in case
  // it's been placed in a fixture at a different venue.
  venueCandidates: ["Venue", "Target Venue", "Asset Venue"],
};

// ================= FIXTURE KEYS =================
const FIXTURE_KEY_FIELD_CANDIDATES = [
  "Match ID",
  "Fixture Key",
  "Fixture",
  "Key",
  "ID",
  "Fixture ID",
  "Name",
];

// ================= CORE HELPERS =================
function assertEnv() {
  const missing = [];
  if (!TOKEN) missing.push("REACT_APP_AIRTABLE_TOKEN");
  if (!BASE_ID) missing.push("REACT_APP_AIRTABLE_BASE_ID");
  if (!TABLE_ID) missing.push("REACT_APP_AIRTABLE_TABLE_ID");
  if (!ASSETS_TABLE_ID) missing.push("REACT_APP_AIRTABLE_ASSETS_TABLE_ID");
  if (!FIXTURES_TABLE_ID) missing.push("REACT_APP_AIRTABLE_FIXTURES_TABLE_ID");
  if (missing.length) throw new Error(`Missing env vars: ${missing.join(", ")}`);
}

function assertInGameTable() {
  if (!IN_GAME_TABLE_ID) {
    throw new Error("Missing env var: REACT_APP_AIRTABLE_INGAME_TABLE_ID");
  }
}

function assertIptvTable() {
  if (!IPTV_TABLE_ID) {
    throw new Error("Missing env var: REACT_APP_AIRTABLE_IPTV_TABLE_ID");
  }
}

function assertLedTable() {
  if (!LED_TABLE_ID) {
    throw new Error("Missing env var: REACT_APP_AIRTABLE_LEDENTITLEMENTS_TABLE_ID");
  }
}

function assertTeamListTable() {
  if (!TEAMLIST_TABLE_ID) {
    throw new Error("Missing env var: REACT_APP_AIRTABLE_TEAMLIST_TABLE_ID");
  }
}

function assertViewConfigTable() {
  if (!VIEWCONFIG_TABLE_ID) {
    throw new Error("Missing env var: REACT_APP_AIRTABLE_VIEWCONFIG_TABLE_ID");
  }
}

function tableUrl(tableId, path = "") {
  return `https://api.airtable.com/v0/${BASE_ID}/${tableId}${path}`;
}

async function airtableFetch(url, options = {}) {
  assertEnv();

  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = text;
  }

  if (!res.ok) {
    const payload = typeof data === "string" ? data : JSON.stringify(data);
    throw new Error(`Airtable error ${res.status}: ${payload}`);
  }

  return data;
}

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function stripWrappingQuotes(s) {
  let str = String(s ?? "").trim();
  if (!str) return "";

  str = str.replace(/""/g, '"');
  str = str.replace(/[""]/g, '"').replace(/['']/g, "'");

  for (let i = 0; i < 5; i++) {
    const m = str.match(/^(['"])(.*)\1$/);
    if (!m) break;
    str = (m[2] ?? "").trim();
  }

  return str.trim();
}

function asMultiSelectArray(v) {
  const out = [];

  const pushStr = (s) => {
    if (s == null) return;

    let str = stripWrappingQuotes(s);
    if (!str) return;

    if (str.includes(",")) {
      str
        .split(",")
        .map((x) => stripWrappingQuotes(x))
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((x) => out.push(x));
    } else {
      out.push(str);
    }
  };

  const coerceItem = (item) => {
    if (item == null) return;

    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
      pushStr(item);
      return;
    }

    if (Array.isArray(item)) {
      item.forEach(coerceItem);
      return;
    }

    if (typeof item === "object") {
      if (item.value != null) return pushStr(item.value);
      if (item.label != null) return pushStr(item.label);
      if (item.name != null) return pushStr(item.name);
      if (Array.isArray(item.values)) return item.values.forEach(coerceItem);
      return;
    }

    pushStr(item);
  };

  if (v == null || v === "") return [];
  if (typeof v === "string") {
    pushStr(v);
  } else if (Array.isArray(v)) {
    v.forEach(coerceItem);
  } else if (typeof v === "object") {
    coerceItem(v);
  } else {
    pushStr(v);
  }

  return Array.from(
    new Set(out.map((x) => stripWrappingQuotes(x)).map((x) => x.trim()).filter(Boolean))
  );
}

function filterToAllowedMultiSelect(arr, allowedSet) {
  if (!allowedSet) return arr;
  return (arr || [])
    .map((x) => stripWrappingQuotes(x))
    .map((x) => x.trim())
    .filter((x) => x && allowedSet.has(x));
}

function pickFirst(fields, candidates) {
  for (const c of candidates) if (fields?.[c] != null) return fields[c];
  return null;
}

function pickThumb(fields) {
  const val = pickFirst(fields, ASSET_FIELDS.thumbCandidates);
  // Use 'large' thumbnail (512x512) for better quality
  if (Array.isArray(val) && val[0]?.thumbnails?.large?.url) return val[0].thumbnails.large.url;
  if (Array.isArray(val) && val[0]?.thumbnails?.small?.url) return val[0].thumbnails.small.url;
  if (Array.isArray(val) && val[0]?.url) return val[0].url;
  if (typeof val === "string") return val;
  return null;
}

function pickTitle(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.titleCandidates);
  return typeof v === "string" ? v : v?.toString?.() || "(asset)";
}

function pickDestination(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.destinationCandidates);
  return typeof v === "string" ? v : v?.toString?.() || "";
}

// ✅ NEW: Pick Has Alpha field (checkbox)
function pickHasAlpha(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.hasAlphaCandidates);
  // Airtable checkboxes return true when checked, undefined when unchecked
  return v === true;
}

// ✅ NEW: Pick Has Audio field (checkbox)
function pickHasAudio(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.hasAudioCandidates);
  // Airtable checkboxes return true when checked, undefined when unchecked
  return v === true;
}

// ✅ NEW: Pick Preview URL field (for video playback)
// Handles rich text markdown links like [Title](URL)
function pickPreviewUrl(fields) {
  let v = pickFirst(fields, ASSET_FIELDS.previewUrlCandidates);
  if (!v) return null;
  
  if (typeof v === "string") {
    // Decode URL-encoded characters first (e.g., %60 -> backtick)
    try {
      v = decodeURIComponent(v);
    } catch (e) {
      // If decoding fails, continue with original string
    }
    
    // Check for markdown link format: [text](url)
    const markdownMatch = v.match(/\[([^\]]*)\]\(([^)]+)\)/);
    if (markdownMatch) {
      let url = markdownMatch[2].trim();
      // Fix common URL issues
      url = url.replace(/^https?:\/(?!\/)/, (match) => match + '/'); // Fix https:/ -> https://
      return url;
    }
    
    // Plain URL - also fix common issues
    let url = v.trim();
    url = url.replace(/^https?:\/(?!\/)/, (match) => match + '/');
    return url;
  }
  
  if (Array.isArray(v) && v.length > 0) {
    return v[0]?.url || v[0];
  }
  return null;
}

// ✅ NEW: Pick Asset Spec Summary field
function pickSpecSummary(fields) {
  return pickFirst(fields, ASSET_FIELDS.specSummaryCandidates) || null;
}

// ✅ NEW: Pick Asset Download URL (raw)
function pickDownloadUrl(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.downloadUrlCandidates);
  if (!v) return null;
  if (typeof v === "string") return v.trim();
  if (Array.isArray(v) && v.length > 0) {
    return v[0]?.url || v[0];
  }
  return null;
}

// ✅ NEW: Pick Asset Type field
function pickAssetType(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.assetTypeCandidates);
  if (!v) return null;
  if (typeof v === "string") return v.trim().toLowerCase();
  return null;
}

// ✅ NEW: Pick Submitter Email for contact functionality
function pickSubmitterEmail(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.submitterEmailCandidates);
  return typeof v === "string" ? v.trim() : null;
}

// ✅ NEW: Pick Lumen8 ID for operator linking
function pickLumenId(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.lumenIdCandidates);
  return typeof v === "string" ? v.trim() : null;
}

// ✅ NEW: Pick Archived status for hiding old assets
function pickArchived(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.archivedCandidates);
  return v === true || v === "true" || v === 1;
}

// ✅ NEW: Pick Round Allocations JSON for TVC round tagging
function pickRoundAllocations(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.roundAllocationsCandidates);
  return typeof v === "string" ? v.trim() : (v || "");
}

// ✅ NEW: Pick Uploader Type (Club, Commercial, etc.)
function pickUploaderType(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.uploaderTypeCandidates);
  return typeof v === "string" ? v.trim() : "";
}

// ✅ NEW: Pick content metadata for commercial playlist enrichment
function pickTier(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.tierCandidates);
  return typeof v === "string" ? v.trim() : "";
}

function pickCategory(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.categoryCandidates);
  return typeof v === "string" ? v.trim() : "";
}

function pickPrimaryColour(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.primaryColourCandidates);
  return typeof v === "string" ? v.trim() : "";
}

function pickFilePath(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.filePathCandidates);
  return typeof v === "string" ? v.trim() : "";
}

// Venue — picks MCG/Marvel/SCG etc from the Master Content Database.
// Used by TVC view to tag each asset with its target venue so the team
// can spot mismatches against the fixture venue.
function pickVenue(fields) {
  const v = pickFirst(fields, ASSET_FIELDS.venueCandidates);
  return typeof v === "string" ? v.trim() : (Array.isArray(v) && v.length > 0 ? String(v[0]).trim() : "");
}

function extractUnknownFieldName(err) {
  const msg = String(err?.message || err || "");
  const m = msg.match(/Unknown field name:\s*\\?"([^"\\]+)\\?"/i);
  return m?.[1] || "";
}

function pickFieldValue(fields, candidates) {
  for (const name of candidates || []) {
    if (fields?.[name] != null) return fields[name];
  }
  return undefined;
}

function setLinked(fields, field, id) {
  fields[field] = id ? [id] : [];
}

function isLikelyRequiredFieldError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    msg.includes("field") &&
    (msg.includes("required") || msg.includes("cannot be blank") || msg.includes("is empty"))
  );
}

// ================= VIEW HELPERS =================
function getVenueFromViewName(viewName) {
  const v = String(viewName || "").trim();
  if (!v) return "ALL";
  if (v.endsWith("_MCG")) return "MCG";
  if (v.endsWith("_MRV")) return "MRV";
  return "ALL";
}

function ensureStringCellFormatParams(params) {
  if (!params || typeof params.set !== "function") return params;
  const cf = params.get?.("cellFormat");
  if (String(cf || "").toLowerCase() === "string") {
    if (!params.get("timeZone")) params.set("timeZone", AIRTABLE_STRING_FORMAT_TZ);
    if (!params.get("userLocale")) params.set("userLocale", AIRTABLE_STRING_FORMAT_LOCALE);
  }
  return params;
}

function buildFieldsParamForVenue(tableId, venue) {
  const isIptv = isIptvTableId(tableId);
  const isTeamList = isTeamListTableId(tableId);

  // ✅ Audit fields for row history (always included)
  const auditFields = [
    FIELDS.lastModifiedTime,
    FIELDS.venueFlowEditor, // Custom field tracking actual VenueFlow user
  ];

  if (isIptv) {
    return [
      FIELDS.fixture,
      FIELDS.cue,
      FIELDS.category,
      FIELDS.name,
      FIELDS.clientSponsor,
      FIELDS.contentStatus,
      FIELDS.notes,
      FIELDS.iptvGfx,
      FIELDS.rotations,
      FIELDS.selectTiming,
      FIELDS.selectLocation,
      FIELDS.clubInstructions,
      ...auditFields,
    ];
  }

  const core = [
    FIELDS.fixture,
    FIELDS.cue,
    FIELDS.category,
    FIELDS.name,
    ...(isTeamList ? [FIELDS.playerNumber] : []), // ✅ Player Number only for Team List
    ...(isTeamList ? [FIELDS.destinationVenue] : []), // ✅ Destination Venue for Team List
    FIELDS.clientSponsor,
    FIELDS.notes,
    FIELDS.contentStatus,
    FIELDS.clubInstructions,
    FIELDS.bigGfxVideos,
    FIELDS.cornerGfxVideos,
    FIELDS.ledAllTypes,
    FIELDS.bigTvc,
    FIELDS.cornerTvc, // ✅ ADD: Corner Screen - TVC for Marvel
    ...auditFields,
  ];

  // ✅ ADD: Rotations and Bonus Priority for LED tables
  const withLed = isLedTableId(tableId) ? [...core, FIELDS.allocationId, FIELDS.rotations, FIELDS.bonusPriority, FIELDS.breaks] : core;

  if (venue === "MCG") {
    return [...withLed, FIELDS.mcgInterchange, FIELDS.mcg360, FIELDS.mcgTiledLed];
  }

  if (venue === "MRV") {
    return [
      ...withLed,
      FIELDS.superFull,
      FIELDS.super169,
      FIELDS.superSide,
      FIELDS.marvelInterchange,
      FIELDS.marvel360,
      FIELDS.marvelTiledLed,
    ];
  }

  return [
    ...withLed,
    FIELDS.superFull,
    FIELDS.super169,
    FIELDS.superSide,
    FIELDS.mcgInterchange,
    FIELDS.marvelInterchange,
    FIELDS.marvel360,
    FIELDS.mcg360,
    FIELDS.mcgTiledLed,
    FIELDS.marvelTiledLed,
  ];
}

// ================= VIEW CONFIG =================
export async function fetchAppViewsConfig({ includeInactive = false } = {}) {
  assertViewConfigTable();

  const params = new URLSearchParams({
    pageSize: "100",
    cellFormat: "string",
    "sort[0][field]": VIEWCONFIG_FIELDS.viewName,
    "sort[0][direction]": "asc",
  });

  ensureStringCellFormatParams(params);

  params.append("fields[]", VIEWCONFIG_FIELDS.viewName);
  params.append("fields[]", VIEWCONFIG_FIELDS.fixtureKey);
  params.append("fields[]", VIEWCONFIG_FIELDS.activeOnly);

  if (!includeInactive) {
    params.set("filterByFormula", `{${VIEWCONFIG_FIELDS.activeOnly}}=TRUE()`);
  }

  const data = await airtableFetch(`${tableUrl(VIEWCONFIG_TABLE_ID)}?${params.toString()}`);

  const rows = (data.records || []).map((r) => {
    const f = r.fields || {};
    const viewName = String(f[VIEWCONFIG_FIELDS.viewName] || "").trim();
    const fixtureKey = String(f[VIEWCONFIG_FIELDS.fixtureKey] || "").trim();

    const rawActive = f[VIEWCONFIG_FIELDS.activeOnly];
    const activeOnly =
      rawActive === true ||
      String(rawActive || "")
        .trim()
        .toLowerCase() === "true";

    return { id: r.id, viewName, fixtureKey, activeOnly };
  });

  const filtered = includeInactive
    ? rows
    : rows.filter((x) => x.activeOnly && x.viewName && x.fixtureKey);

  const dedup = new Map();
  for (const r of filtered) {
    if (!r.viewName) continue;
    dedup.set(r.viewName, r);
  }

  return Array.from(dedup.values()).sort((a, b) => a.viewName.localeCompare(b.viewName));
}

// ================= FIXTURES =================

/**
 * Fetch upcoming fixtures with dates, venues, and team info
 * Used for the welcome dashboard to show next match countdown
 */
export async function fetchUpcomingFixtures() {
  if (!FIXTURES_TABLE_ID) {
    console.warn("FIXTURES_TABLE_ID not configured");
    return [];
  }

  try {
    // Known field names from St Kilda's Fixtures table
    const FIXTURE_FIELDS = {
      dateTime: "Date / Time",      // Primary date/time field
      matchId: "Match ID",          // Fixture identifier (e.g., "RD3_STK_v_COL_MCG")
      venue: "Venue",
      homeTeam: "Home Team",
      awayTeam: "Away Team", 
      round: "Round",
    };
    
    // Fallback candidates for other club setups
    const dateFieldCandidates = [FIXTURE_FIELDS.dateTime, "Date", "Match Date", "Game Date", "Fixture Date", "DateTime", "Start Date"];
    const matchIdCandidates = [FIXTURE_FIELDS.matchId, ...FIXTURE_KEY_FIELD_CANDIDATES];
    const venueFieldCandidates = [FIXTURE_FIELDS.venue, "Stadium", "Ground", "Location"];
    const homeTeamCandidates = [FIXTURE_FIELDS.homeTeam, "Home", "Team 1", "Home Club"];
    const awayTeamCandidates = [FIXTURE_FIELDS.awayTeam, "Away", "Team 2", "Away Club"];
    const roundCandidates = [FIXTURE_FIELDS.round, "Rd", "Round Number", "Match Round"];
    
    const params = new URLSearchParams({
      pageSize: "50",
      cellFormat: "string",
      "sort[0][field]": FIXTURE_FIELDS.dateTime,
      "sort[0][direction]": "asc",												
								  
    });
    ensureStringCellFormatParams(params);
    
    // Don't request specific fields[] - some candidate names may not exist on this table,
    // and Airtable returns 422 for unknown field names. Fetch all fields and pick what exists.
    const data = await airtableFetch(`${tableUrl(FIXTURES_TABLE_ID)}?${params.toString()}`);
    
    const fixtures = (data.records || []).map(record => {
      const f = record.fields || {};
      
      // Find values using candidates (prioritized order)
      const findValue = (candidates) => {
        for (const field of candidates) {
          if (f[field]) return f[field];
        }
        return null;
      };
      
      return {
        id: record.id,
        matchId: findValue(matchIdCandidates),
        dateTime: findValue(dateFieldCandidates),
        venue: findValue(venueFieldCandidates),
        homeTeam: findValue(homeTeamCandidates),
        awayTeam: findValue(awayTeamCandidates),
        round: findValue(roundCandidates),
        raw: f,
      };
    });
    
    // Filter to upcoming fixtures only and sort by date
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return fixtures
      .filter(f => {
        if (!f.dateTime) return false; // Skip fixtures without dates
        try {
          const fixtureDate = new Date(f.dateTime);
          return fixtureDate >= today;
        } catch {
          return false;
        }
      })
      .sort((a, b) => {
        return new Date(a.dateTime) - new Date(b.dateTime);
      });
      
  } catch (err) {
    console.warn("Failed to fetch upcoming fixtures:", err);
    return [];
  }
}

export async function resolveFixtureRecordIdByKey(key) {
  const safe = String(key || "").trim();
  if (!safe) return null;

  const safeEsc = safe.replace(/"/g, '\\"');

  for (const field of FIXTURE_KEY_FIELD_CANDIDATES) {
    try {
      const params = new URLSearchParams({
        pageSize: "1",
        filterByFormula: `{${field}}="${safeEsc}"`,
      });

      const data = await airtableFetch(`${tableUrl(FIXTURES_TABLE_ID)}?${params.toString()}`);
      if (data.records?.[0]?.id) return data.records[0].id;
    } catch {
      continue;
    }
  }

  return null;
}

function buildFixtureFilterFormula(fixtureValue) {
  const safe = String(fixtureValue || "").trim();
  const safeEsc = safe.replace(/"/g, '\\"');
  return `OR({${FIELDS.fixture}}="${safeEsc}", FIND("${safeEsc}", ARRAYJOIN({${FIELDS.fixture}})))`;
}

// ================= FETCH ITEMS (shared core) =================
function toFixtureDisplay(val) {
  if (Array.isArray(val)) return val.join(", ");
  if (typeof val === "string") return val;
  return val?.toString?.() || "";
}

function mapRecordToItem(tableId, record, { fixtureOverride } = {}) {
  const { id, fields = {} } = record || {};
  const isIptv = isIptvTableId(tableId);
  const isLed = isLedTableId(tableId);

  const iptvGfxVal = isIptv ? pickFieldValue(fields, IPTV_FIELD_ALIASES.iptvGfx) : undefined;
  const rotationsVal = (isIptv || isLed) ? pickFieldValue(fields, IPTV_FIELD_ALIASES.rotations) : undefined;
  // ✅ Read Bonus Priority directly from field name as fallback
  const bonusPriorityVal = isLed ? (pickFieldValue(fields, IPTV_FIELD_ALIASES.bonusPriority) ?? fields["Bonus Priority"]) : undefined;
  
  // Debug logging for LED fields - ALWAYS log to see what's coming from Airtable
  if (isLed) {
    console.log('[airtable] READ LED record:', id, { 
      rotationsVal, 
      bonusPriorityVal,
      rawRotations: fields["Rotations"],
      rawBonusPriority: fields["Bonus Priority"],
      allFieldKeys: Object.keys(fields)
    });
  }																																																										  
  const timingVal = isIptv ? pickFieldValue(fields, IPTV_FIELD_ALIASES.selectTiming) : undefined;
  const locationVal = isIptv ? pickFieldValue(fields, IPTV_FIELD_ALIASES.selectLocation) : undefined;

  const fixtureValue = fixtureOverride ? String(fixtureOverride) : toFixtureDisplay(fields[FIELDS.fixture]);

  const iptvGfxId =
    isIptv
      ? (Array.isArray(iptvGfxVal)
          ? (iptvGfxVal[0] || null)
          : (typeof iptvGfxVal === "string" ? iptvGfxVal : null))
      : null;

  return {
    id,

    cue: asNumber(fields[FIELDS.cue]),
    category: fields[FIELDS.category] || "",
    fixture: fixtureValue,

    name: fields[FIELDS.name] || "",
    playerNumber: fields[FIELDS.playerNumber] || "",
    clientSponsorName: fields[FIELDS.clientSponsor] || "",
    notes: fields[FIELDS.notes] || "",

    clubInstructions: fields[FIELDS.clubInstructions] || "",
    contentStatus: fields[FIELDS.contentStatus] || "",
    hasContent: fields[FIELDS.hasContent] || "",

    allocationId: fields[FIELDS.allocationId] || "",

    iptvGfxId,
    // ✅ Rotations is now a Number field (changed from Single Select)
    rotations: (isIptv || isLed) ? asNumber(rotationsVal) : null,
    // ✅ Bonus Priority is a number field for LED
    bonusPriority: isLed ? asNumber(bonusPriorityVal) : null,												   
															 
    selectTiming: isIptv ? asMultiSelectArray(timingVal) : [],
    selectLocation: isIptv ? asMultiSelectArray(locationVal) : [],

    bigGfxVideosId: Array.isArray(fields[FIELDS.bigGfxVideos]) ? (fields[FIELDS.bigGfxVideos][0] || null) : null,
    cornerGfxVideosId: Array.isArray(fields[FIELDS.cornerGfxVideos]) ? (fields[FIELDS.cornerGfxVideos][0] || null) : null,
    ledAllTypesId: Array.isArray(fields[FIELDS.ledAllTypes]) ? (fields[FIELDS.ledAllTypes][0] || null) : null,
    bigTvcId: Array.isArray(fields[FIELDS.bigTvc]) ? (fields[FIELDS.bigTvc][0] || null) : null,
    cornerTvcId: Array.isArray(fields[FIELDS.cornerTvc]) ? (fields[FIELDS.cornerTvc][0] || null) : null,

    superFullId: Array.isArray(fields[FIELDS.superFull]) ? (fields[FIELDS.superFull][0] || null) : null,
    super169Id: Array.isArray(fields[FIELDS.super169]) ? (fields[FIELDS.super169][0] || null) : null,
    superSideId: Array.isArray(fields[FIELDS.superSide]) ? (fields[FIELDS.superSide][0] || null) : null,

    mcgInterchangeId: Array.isArray(fields[FIELDS.mcgInterchange]) ? (fields[FIELDS.mcgInterchange][0] || null) : null,
    marvelInterchangeId: Array.isArray(fields[FIELDS.marvelInterchange]) ? (fields[FIELDS.marvelInterchange][0] || null) : null,

    marvel360Id: Array.isArray(fields[FIELDS.marvel360]) ? (fields[FIELDS.marvel360][0] || null) : null,
    mcg360Id: Array.isArray(fields[FIELDS.mcg360]) ? (fields[FIELDS.mcg360][0] || null) : null,

    mcgTiledLedId: Array.isArray(fields[FIELDS.mcgTiledLed]) ? (fields[FIELDS.mcgTiledLed][0] || null) : null,
    marvelTiledLedId: Array.isArray(fields[FIELDS.marvelTiledLed]) ? (fields[FIELDS.marvelTiledLed][0] || null) : null,

    // ✅ Audit fields for row history
    lastModifiedTime: fields[FIELDS.lastModifiedTime] || null,
    venueFlowEditor: fields[FIELDS.venueFlowEditor] || null, // Custom field - actual VenueFlow user who edited

    // ✅ Destination Venue for Team List records (MCG, Marvel Stadium)
    destinationVenue: fields[FIELDS.destinationVenue] || "",
  };
}

function buildSortParamsForTable(tableId) {
  const isIptv = isIptvTableId(tableId);

  const params = new URLSearchParams({
    pageSize: "100",
    "sort[0][field]": FIELDS.category,
    "sort[0][direction]": "asc",
    "sort[1][field]": FIELDS.cue,
    "sort[1][direction]": "asc",
  });

  if (isIptv) {
    params.set("sort[1][field]", FIELDS.rotations);
    params.set("sort[1][direction]", "asc");
  }

  return params;
}

async function fetchWithIptvFieldFallback(tableId, makeUrlFn) {
  const isIptv = isIptvTableId(tableId);

  // Paginated fetch — follows offset tokens to get all records
  const fetchAllPages = async (urlFn) => {
    let allRecords = [];
    let offset = null;
    do {
      const baseUrl = typeof urlFn === "function" ? urlFn() : urlFn;
      const separator = baseUrl.includes("?") ? "&" : "?";
      const url = offset ? `${baseUrl}${separator}offset=${offset}` : baseUrl;
      const data = await airtableFetch(url);
      allRecords = [...allRecords, ...(data.records || [])];
      offset = data.offset || null;
    } while (offset);
    return { records: allRecords };
  };

  if (!isIptv) return fetchAllPages(makeUrlFn);

  try {
    return await fetchAllPages(makeUrlFn);
  } catch (e) {
    const unknown = extractUnknownFieldName(e);
    if (!unknown) throw e;

    const retryFor = (canonical, aliases) => {
      if (unknown !== canonical) return null;
      for (let i = 1; i < (aliases || []).length; i++) {
        const alt = aliases[i];
        if (!alt || alt === canonical) continue;
        return alt;
      }
      return "__REMOVE__";
    };

    const alt =
      retryFor(FIELDS.selectTiming, IPTV_FIELD_ALIASES.selectTiming) ||
      retryFor(FIELDS.selectLocation, IPTV_FIELD_ALIASES.selectLocation) ||
      retryFor(FIELDS.rotations, IPTV_FIELD_ALIASES.rotations) ||
      retryFor(FIELDS.iptvGfx, IPTV_FIELD_ALIASES.iptvGfx);

    if (!alt) throw e;
    if (typeof makeUrlFn !== "function" || makeUrlFn.length < 1) throw e;

    return fetchAllPages(() => makeUrlFn({ unknownField: unknown, altField: alt }));
  }
}

async function fetchItemsFromTable(tableId, options = {}) {
  const viewName = String(options?.viewName || "").trim();
  const venue = getVenueFromViewName(viewName);
  const fieldList = buildFieldsParamForVenue(tableId, venue);

  const params = buildSortParamsForTable(tableId);
  if (viewName) params.set("view", viewName);

  fieldList.forEach((f) => params.append("fields[]", f));

  const urlBuilder = (override) => {
    if (!override) return `${tableUrl(tableId)}?${params.toString()}`;

    const { unknownField, altField } = override || {};
    const rebuilt = fieldList
      .filter((f) => (f !== unknownField ? true : altField !== "__REMOVE__"))
      .map((f) => (f === unknownField ? altField : f));

    const clean = new URLSearchParams();
    params.forEach((v, k) => {
      if (k !== "fields[]") clean.append(k, v);
    });

    rebuilt.forEach((f) => clean.append("fields[]", f));

    return `${tableUrl(tableId)}?${clean.toString()}`;
  };

  const data = await fetchWithIptvFieldFallback(tableId, urlBuilder);
  return (data.records || []).map((r) => mapRecordToItem(tableId, r, {}));
}

// ✅ KEY FIX: This function now correctly handles view-based filtering
async function fetchFixtureItemsFromTable(tableId, fixtureValue, options = {}) {
  const viewName = String(options?.viewName || "").trim();
  const venue = getVenueFromViewName(viewName);
  const fieldList = buildFieldsParamForVenue(tableId, venue);

  const safeFixture = String(fixtureValue || "").trim();
  
  // ✅ FIX: When we have a viewName, use ONLY the view for filtering
  // The Airtable view already filters records to the correct fixture
  // Only apply filterByFormula if we DON'T have a viewName
  const useFilterFormula = !viewName && safeFixture;
  const filterByFormula = useFilterFormula ? buildFixtureFilterFormula(safeFixture) : "";

  const params = buildSortParamsForTable(tableId);
  
  // Only set filterByFormula if not using a view
  if (filterByFormula) {
    params.set("filterByFormula", filterByFormula);
  }
  
  // Always set the view if provided - this is the primary filter method
  if (viewName) {
    params.set("view", viewName);
    console.log(`📋 Using Airtable view "${viewName}" for filtering`);
  }

  fieldList.forEach((f) => params.append("fields[]", f));

  const urlBuilder = (override) => {
    if (!override) return `${tableUrl(tableId)}?${params.toString()}`;

    const { unknownField, altField } = override || {};
    const rebuilt = fieldList
      .filter((f) => (f !== unknownField ? true : altField !== "__REMOVE__"))
      .map((f) => (f === unknownField ? altField : f));

    const clean = new URLSearchParams();
    params.forEach((v, k) => {
      if (k !== "fields[]") clean.append(k, v);
    });

    rebuilt.forEach((f) => clean.append("fields[]", f));

    return `${tableUrl(tableId)}?${clean.toString()}`;
  };

  const data = await fetchWithIptvFieldFallback(tableId, urlBuilder);

  // When using a view, don't override fixture values
  // The records already have correct fixture data from the view
  const fixtureOverride = useFilterFormula && safeFixture ? safeFixture : "";

  return (data.records || []).map((r) =>
    mapRecordToItem(tableId, r, fixtureOverride ? { fixtureOverride } : {})
  );
}

// ✅ Existing export (Out of Game)
export async function fetchFixtureItems(fixtureValue, options = {}) {
  return fetchFixtureItemsFromTable(TABLE_ID, fixtureValue, options);
}

// ✅ In Game export
export async function fetchFixtureItemsInGame(fixtureValue, options = {}) {
  assertInGameTable();
  return fetchFixtureItemsFromTable(IN_GAME_TABLE_ID, fixtureValue, options);
}

// ✅ IPTV export
export async function fetchFixtureItemsIptv(fixtureValue, options = {}) {
  assertIptvTable();
  return fetchFixtureItemsFromTable(IPTV_TABLE_ID, fixtureValue, options);
}

// ✅ LED Entitlements export
export async function fetchFixtureItemsLed(fixtureValue, options = {}) {
  assertLedTable();
  return fetchFixtureItemsFromTable(LED_TABLE_ID, fixtureValue, options);
}

/**
 * Fetch ALL LED Commercial Entitlement rows from the club LED table.
 * These are rows created via the LED Playlist "Add Content" button.
 * Returns raw Airtable records (not mapped to item format).
 */
export async function fetchAllLedCommercialEntitlements() {
  assertLedTable();
  const formula = 'OR({Category} = "In-Game LED Commercial Entitlements", {Category} = "Bonus Entitlement")';
  const all = [];
  let offset = null;
  do {
    let url = `${tableUrl(LED_TABLE_ID)}?pageSize=100&cellFormat=string&timeZone=Australia%2FMelbourne&userLocale=en-au&filterByFormula=${encodeURIComponent(formula)}`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    if (data?.records) all.push(...data.records);
    offset = data?.offset || null;
  } while (offset);
  return all;
}

// ✅ Team List export (season-wide; fixture filter NOT applied)
export async function fetchFixtureItemsTeam(_fixtureValue, options = {}) {
  assertTeamListTable();
  return fetchItemsFromTable(TEAMLIST_TABLE_ID, options);
}

export async function fetchFixtureItemsTeamList(fixtureValue, options = {}) {
  return fetchFixtureItemsTeam(fixtureValue, options);
}

// ================= ASSETS =================
export async function searchAssets(q, { pageSize = 25, destinations = null, includeArchived = false } = {}) {
  const query = String(q || "").trim().toLowerCase();
  const params = new URLSearchParams({ pageSize: String(pageSize) });

  // Base formula parts
  const formulaParts = [];
  
  // Add archived filter (exclude archived by default)
  if (!includeArchived) {
    formulaParts.push('NOT({Archived})');
  }

  if (query) {
    const safe = query.replace(/"/g, '\\"');
    
    // Build search formula
    let searchFormula = `OR(FIND("${safe}", LOWER({Lumen8 ID})), FIND("${safe}", LOWER({Asset ID})), FIND("${safe}", LOWER({Name})), FIND("${safe}", LOWER({File/Folder Name})))`;
    formulaParts.push(searchFormula);
    
    // Add destination filter if provided
    if (destinations && destinations.length > 0) {
      const destFormula = destinations.map(d => `{Asset Destination} = "${d}"`).join(', ');
      formulaParts.push(`OR(${destFormula})`);
    }
  } else if (destinations && destinations.length > 0) {
    // If no search query but destinations provided, filter by destination only
    const destFormula = destinations.map(d => `{Asset Destination} = "${d}"`).join(', ');
    formulaParts.push(`OR(${destFormula})`);
  }
  
  // Combine all formula parts with AND
  if (formulaParts.length > 0) {
    params.set("filterByFormula", formulaParts.length === 1 ? formulaParts[0] : `AND(${formulaParts.join(', ')})`);
  }

  const data = await airtableFetch(`${tableUrl(ASSETS_TABLE_ID)}?${params.toString()}`);

  return (data.records || []).map(({ id, fields = {} }) => ({
    id,
    lumenId: pickLumenId(fields),  // Include Lumen8 ID for operator linking
    title: pickTitle(fields),
    name: pickTitle(fields),  // Alias for compatibility
    thumb: pickThumb(fields),
    destination: pickDestination(fields),
  }));
}

export async function fetchAssetsByIds(ids) {
  const uniq = Array.from(new Set((ids || []).filter(Boolean)));
  const out = new Map();
  if (!uniq.length) return out;

  for (let i = 0; i < uniq.length; i += 20) {
    const chunk = uniq.slice(i, i + 20);
    const formula = `OR(${chunk.map((id) => `RECORD_ID()="${id}"`).join(",")})`;
    const params = new URLSearchParams({ filterByFormula: formula });

    const data = await airtableFetch(`${tableUrl(ASSETS_TABLE_ID)}?${params.toString()}`);
    (data.records || []).forEach((r) => {
      out.set(r.id, {
        id: r.id,
        title: pickTitle(r.fields),
        thumb: pickThumb(r.fields),
        destination: pickDestination(r.fields),
      });
    });
  }

  return out;
}

// ✅ NEW: Fetch ALL assets from the Assets table
// This ensures the asset picker is always populated, even for empty rows
export async function fetchAllAssets() {
  const out = new Map();
  let offset = null;

  do {
    const params = new URLSearchParams({
      pageSize: "100",
    });

    if (offset) {
      params.set("offset", offset);
    }

    const data = await airtableFetch(`${tableUrl(ASSETS_TABLE_ID)}?${params.toString()}`);

    (data.records || []).forEach((r) => {
      out.set(r.id, {
        id: r.id,
        title: pickTitle(r.fields),
        lumenId: pickLumenId(r.fields), // ✅ Lumen8 ID for operator linking
        thumb: pickThumb(r.fields),
        destination: pickDestination(r.fields),
        // Raw destination text — skips linked record IDs (recXXX)
        destinationText: (() => {
          for (const k of ["Asset Destination (Text)", "Destination (Text)", "Asset Destination", "Destination"]) {
            const v = r.fields?.[k];
            if (typeof v === "string" && v && !v.startsWith("rec")) return v;
            if (Array.isArray(v) && v.length > 0 && typeof v[0] === "string" && !v[0].startsWith("rec")) return v[0];
          }
          return "";
        })(),
        hasAlpha: pickHasAlpha(r.fields),
        hasAudio: pickHasAudio(r.fields),
        previewUrl: pickPreviewUrl(r.fields), // ✅ For video playback in gallery
        specSummary: pickSpecSummary(r.fields), // ✅ Asset Spec Summary from Airtable
        downloadUrl: pickDownloadUrl(r.fields), // ✅ Asset Download URL (raw)
        assetType: pickAssetType(r.fields), // ✅ Asset Type (video/image)
        submitterEmail: pickSubmitterEmail(r.fields), // ✅ Submitter Email for contact
        archived: pickArchived(r.fields), // ✅ Archived status for hiding old assets
        roundAllocations: pickRoundAllocations(r.fields), // ✅ Round Allocations JSON for TVC
        uploaderType: pickUploaderType(r.fields), // ✅ Uploader Type (Club/Commercial)
        tier: pickTier(r.fields), // ✅ Content tier for commercial playlists
        category: pickCategory(r.fields), // ✅ Content category for commercial playlists
        primaryColour: pickPrimaryColour(r.fields), // ✅ Primary colour for commercial playlists
        filePath: pickFilePath(r.fields), // ✅ Asset FilePath for Send Assets delivery
        venue: pickVenue(r.fields), // ✅ Venue (MCG/Marvel/etc) from Master Content Database — TVC badge
        duration: parseFloat(r.fields?.['Duration']) || 0, // ✅ Actual content duration from asset record
        groupedAssetLinks: r.fields?.['Grouped Asset Links'] || '', // ✅ JSON string for tiled/360 level files
        // ✅ Store created time for client-side sorting
        createdTime: r.createdTime || null,
      });
    });

    offset = data.offset || null;
  } while (offset);

  return out;
}

// ================= MUTATIONS =================
function buildFieldsFromPayload(tableId, payload) {
  const fields = {};

  // Only set fields that are explicitly in the payload
  if (payload.name !== undefined) {
    fields[FIELDS.name] = payload.name ?? "";
  }
  if (payload.clientSponsorName !== undefined || payload.clientSponsor !== undefined) {
    fields[FIELDS.clientSponsor] = payload.clientSponsorName || payload.clientSponsor || "";
  }
  if (payload.notes !== undefined) {
    fields[FIELDS.notes] = payload.notes || "";
  }
  if (payload.clubInstructions !== undefined) {
    fields[FIELDS.clubInstructions] = payload.clubInstructions || "";
  }
  if (payload.category !== undefined) {
    fields[FIELDS.category] = payload.category || "";
  }
  if (payload.cue !== undefined) {
    fields[FIELDS.cue] = asNumber(payload.cue);
  }

  // ✅ Player Number only for Team List
  if (isTeamListTableId(tableId) && payload.playerNumber !== undefined) {
    fields[FIELDS.playerNumber] = payload.playerNumber || "";
  }
  
  // ✅ Destination Venue for Team List (determines which view the row appears in)
  if (isTeamListTableId(tableId) && payload.destinationVenue !== undefined) {
    fields[FIELDS.destinationVenue] = payload.destinationVenue || "";
    console.log("[buildFieldsFromPayload] Setting Destination Venue:", payload.destinationVenue, "Field name:", FIELDS.destinationVenue);
  }

  if (isLedTableId(tableId)) {
    if (payload.allocationId !== undefined) {
      fields[FIELDS.allocationId] = payload.allocationId || "";
    }
    // Breaks (quarters) — multi-select: Q1, Q2, Q3, Q4
    // Only include if user selected quarters (field may not exist in all clubs yet)
    if (payload.breaks !== undefined && Array.isArray(payload.breaks) && payload.breaks.length > 0) {
      const arr = asMultiSelectArray(payload.breaks);
      const LED_BREAKS_SET = new Set(["Q1", "Q2", "Q3", "Q4"]);
      const filtered = arr.filter(x => LED_BREAKS_SET.has(x));
      if (filtered.length > 0) {
        fields[FIELDS.breaks] = filtered;
      }
    }
  }

  if (isIptvTableId(tableId)) {
    // ✅ Rotations is now a Number field (changed from Single Select)
    if (payload.rotations !== undefined) {
      const rotNum = asNumber(payload.rotations);
      if (rotNum > 0) {
        fields[FIELDS.rotations] = rotNum;
      }
    }

    if (payload.selectTiming !== undefined) {
      const arr = asMultiSelectArray(payload.selectTiming);
      fields[FIELDS.selectTiming] = filterToAllowedMultiSelect(arr, IPTV_TIMING_SET);
    }

    if (payload.selectLocation !== undefined) {
      const arr = asMultiSelectArray(payload.selectLocation);
      fields[FIELDS.selectLocation] = filterToAllowedMultiSelect(arr, IPTV_LOCATION_SET);
    }
  }

  // ✅ Rotations for LED table too - now a Number field
  if (isLedTableId(tableId) && payload.rotations !== undefined) {
    const rotNum = asNumber(payload.rotations);
    // Only set field if there's a positive value (omit for 0/empty to clear)
    if (rotNum > 0) {
      fields[FIELDS.rotations] = rotNum;
    }
    console.log('[airtable] Setting Rotations:', payload.rotations, '→', rotNum > 0 ? rotNum : '(omitted)');
  }

  // ✅ Bonus Priority for LED table - Number field
  if (isLedTableId(tableId) && payload.bonusPriority !== undefined) {
    const bpNum = asNumber(payload.bonusPriority);
    // Only set field if there's a positive value (omit for 0/empty to clear)
    if (bpNum > 0) {
      fields[FIELDS.bonusPriority] = bpNum;
    }
    console.log('[airtable] Setting Bonus Priority:', payload.bonusPriority, '→', bpNum > 0 ? bpNum : '(omitted)');
  }
													
																	 
																 
   

  // ✅ VenueFlow Editor - track who made the edit (from Supabase user)
  if (payload.venueFlowEditor !== undefined) {
    fields[FIELDS.venueFlowEditor] = payload.venueFlowEditor || "";
  }

  return fields;
}

function applyLinkedAssets(tableId, fields, payload) {
  // IPTV table only has IPTV GFX field
  if (isIptvTableId(tableId)) {
    if (payload.iptvGfxId !== undefined || payload.iptvGfx !== undefined) {
      setLinked(fields, FIELDS.iptvGfx, payload.iptvGfxId || payload.iptvGfx || null);
    }
    return;
  }

  // TVC Content (In-Game) table - only TVC fields
  if (isInGameTableId(tableId)) {
    if (payload.bigTvcId !== undefined) setLinked(fields, FIELDS.bigTvc, payload.bigTvcId);
    if (payload.cornerTvcId !== undefined) setLinked(fields, FIELDS.cornerTvc, payload.cornerTvcId);
    return;
  }

  // LED Entitlements (In-Game Commercial) table - only LED field
  if (isLedTableId(tableId)) {
    if (payload.ledAllTypesId !== undefined) {
      setLinked(fields, FIELDS.ledAllTypes, payload.ledAllTypesId);
    }
    return;
  }

  // Team List table - Big/Corner GFX, LED, and SuperScreens (Marvel)
  if (isTeamListTableId(tableId)) {
    if (payload.bigGfxVideosId !== undefined) setLinked(fields, FIELDS.bigGfxVideos, payload.bigGfxVideosId);
    if (payload.cornerGfxVideosId !== undefined) setLinked(fields, FIELDS.cornerGfxVideos, payload.cornerGfxVideosId);
    if (payload.ledAllTypesId !== undefined) setLinked(fields, FIELDS.ledAllTypes, payload.ledAllTypesId);
    // SuperScreens — Marvel Team List needs these
    if (payload.superFullId !== undefined) setLinked(fields, FIELDS.superFull, payload.superFullId);
    if (payload.super169Id !== undefined) setLinked(fields, FIELDS.super169, payload.super169Id);
    if (payload.superSideId !== undefined) setLinked(fields, FIELDS.superSide, payload.superSideId);
    return;
  }

  // Game Timeline (Out of Game) table - all screen fields except TVC
  if (payload.bigGfxVideosId !== undefined) setLinked(fields, FIELDS.bigGfxVideos, payload.bigGfxVideosId);
  if (payload.cornerGfxVideosId !== undefined) setLinked(fields, FIELDS.cornerGfxVideos, payload.cornerGfxVideosId);
  if (payload.ledAllTypesId !== undefined) setLinked(fields, FIELDS.ledAllTypes, payload.ledAllTypesId);
  if (payload.superFullId !== undefined) setLinked(fields, FIELDS.superFull, payload.superFullId);
  if (payload.super169Id !== undefined) setLinked(fields, FIELDS.super169, payload.super169Id);
  if (payload.superSideId !== undefined) setLinked(fields, FIELDS.superSide, payload.superSideId);
  if (payload.mcgInterchangeId !== undefined) setLinked(fields, FIELDS.mcgInterchange, payload.mcgInterchangeId);
  if (payload.marvelInterchangeId !== undefined) setLinked(fields, FIELDS.marvelInterchange, payload.marvelInterchangeId);
  if (payload.marvel360Id !== undefined) setLinked(fields, FIELDS.marvel360, payload.marvel360Id);
  if (payload.mcg360Id !== undefined) setLinked(fields, FIELDS.mcg360, payload.mcg360Id);
  if (payload.mcgTiledLedId !== undefined) setLinked(fields, FIELDS.mcgTiledLed, payload.mcgTiledLedId);
  if (payload.marvelTiledLedId !== undefined) setLinked(fields, FIELDS.marvelTiledLed, payload.marvelTiledLedId);
}

async function maybeResolveFixtureId(payload, { requireFixture }) {
  const raw = String(payload?.fixture || "").trim();
  if (!raw) {
    if (requireFixture) throw new Error("Missing fixture on payload");
    return null;
  }

  const fixtureId = await resolveFixtureRecordIdByKey(raw);
  if (!fixtureId && requireFixture) throw new Error(`Fixture not found: ${raw}`);
  return fixtureId || null;
}

function iptvRetrySwapOrOmitFields(retryFields, unknown) {
  const swapOrOmit = (canonical, aliases) => {
    if (unknown !== canonical) return false;
    const alt = (aliases || []).find((x) => x && x !== canonical);
    if (alt) {
      retryFields[alt] = retryFields[canonical];
      delete retryFields[canonical];
      return true;
    }
    delete retryFields[canonical];
    return true;
  };

  return (
    swapOrOmit(FIELDS.selectTiming, IPTV_FIELD_ALIASES.selectTiming) ||
    swapOrOmit(FIELDS.selectLocation, IPTV_FIELD_ALIASES.selectLocation) ||
    swapOrOmit(FIELDS.rotations, IPTV_FIELD_ALIASES.rotations) ||
    swapOrOmit(FIELDS.iptvGfx, IPTV_FIELD_ALIASES.iptvGfx)
  );
}

async function createRowInTable(tableId, payload, { requireFixture = true } = {}) {
  const fields = buildFieldsFromPayload(tableId, payload);
  fields[FIELDS.contentStatus] = payload.contentStatus || STATUS.NEW_SUBMISSION;

  const fixtureId = await maybeResolveFixtureId(payload, { requireFixture });
  if (fixtureId) setLinked(fields, FIELDS.fixture, fixtureId);
  else setLinked(fields, FIELDS.fixture, null);

  applyLinkedAssets(tableId, fields, payload);

  if (isIptvTableId(tableId)) {
    if (Array.isArray(fields[FIELDS.selectTiming]) && fields[FIELDS.selectTiming].length === 0) {
      delete fields[FIELDS.selectTiming];
    }
    if (
      Array.isArray(fields[FIELDS.selectLocation]) &&
      fields[FIELDS.selectLocation].length === 0
    ) {
      delete fields[FIELDS.selectLocation];
    }
  }

  try {
    // DEBUG: Log fields being sent to Airtable for Team List
    if (isTeamListTableId(tableId)) {
      console.log("[createRowInTable] Team List fields being sent:", JSON.stringify(fields, null, 2));
    }
    const created = await airtableFetch(tableUrl(tableId), {
      method: "POST",
      body: JSON.stringify({ records: [{ fields }] }),
    });
    return created?.records?.[0] || null;
  } catch (e) {
    if (isIptvTableId(tableId)) {
      const unknown = extractUnknownFieldName(e);
      if (unknown) {
        const retryFields = { ...fields };
        const did = iptvRetrySwapOrOmitFields(retryFields, unknown);
        if (did) {
          const created = await airtableFetch(tableUrl(tableId), {
            method: "POST",
            body: JSON.stringify({ records: [{ fields: retryFields }] }),
          });
          return created?.records?.[0] || null;
        }
      }
    }

    // LED: if unknown field (e.g. "Breaks" not created yet), retry without it
    if (isLedTableId(tableId)) {
      const unknown = extractUnknownFieldName(e);
      if (unknown) {
        console.warn(`[createRowInTable] LED unknown field "${unknown}" — retrying without it`);
        const retryFields = { ...fields };
        delete retryFields[unknown];
        const created = await airtableFetch(tableUrl(tableId), {
          method: "POST",
          body: JSON.stringify({ records: [{ fields: retryFields }] }),
        });
        return created?.records?.[0] || null;
      }
    }

    if (isLikelyRequiredFieldError(e) && !String(fields[FIELDS.name] || "").trim()) {
      const retryFields = { ...fields, [FIELDS.name]: "New item" };
      const created = await airtableFetch(tableUrl(tableId), {
        method: "POST",
        body: JSON.stringify({ records: [{ fields: retryFields }] }),
      });
      return created?.records?.[0] || null;
    }

    throw e;
  }
}

async function updateRowInTable(tableId, recordId, payload, { requireFixture = true } = {}) {
  if (!recordId) throw new Error("Missing recordId for update");

  const fields = buildFieldsFromPayload(tableId, payload);
  // ✅ Only set contentStatus if explicitly provided - don't auto-change status on edits
  if (payload.contentStatus) {
    fields[FIELDS.contentStatus] = payload.contentStatus;
  }

  const fixtureId = await maybeResolveFixtureId(payload, { requireFixture });
  if (fixtureId) setLinked(fields, FIELDS.fixture, fixtureId);
  else if (!requireFixture) {
    delete fields[FIELDS.fixture];
  }

  applyLinkedAssets(tableId, fields, payload);

  try {
    const updated = await airtableFetch(tableUrl(tableId), {
      method: "PATCH",
      body: JSON.stringify({ records: [{ id: recordId, fields }] }),
    });
    return updated?.records?.[0] || null;
  } catch (e) {
    if (isIptvTableId(tableId)) {
      const unknown = extractUnknownFieldName(e);
      if (unknown) {
        const retryFields = { ...fields };
        const did = iptvRetrySwapOrOmitFields(retryFields, unknown);
        if (did) {
          const updated = await airtableFetch(tableUrl(tableId), {
            method: "PATCH",
            body: JSON.stringify({ records: [{ id: recordId, fields: retryFields }] }),
          });
          return updated?.records?.[0] || null;
        }
      }
    }

    // LED: if unknown field (e.g. "Breaks" not on this club's table), retry without it
    if (isLedTableId(tableId)) {
      const unknown = extractUnknownFieldName(e);
      if (unknown) {
        console.warn(`[updateRowInTable] LED unknown field "${unknown}" — retrying without it`);
        const retryFields = { ...fields };
        delete retryFields[unknown];
        const updated = await airtableFetch(tableUrl(tableId), {
          method: "PATCH",
          body: JSON.stringify({ records: [{ id: recordId, fields: retryFields }] }),
        });
        return updated?.records?.[0] || null;
      }
    }

    if (isLikelyRequiredFieldError(e) && !String(fields[FIELDS.name] || "").trim()) {
      const retryFields = { ...fields, [FIELDS.name]: "New item" };
      const updated = await airtableFetch(tableUrl(tableId), {
        method: "PATCH",
        body: JSON.stringify({ records: [{ id: recordId, fields: retryFields }] }),
      });
      return updated?.records?.[0] || null;
    }

    throw e;
  }
}

async function deleteRowInTable(tableId, recordId) {
  if (!recordId) throw new Error("Missing recordId for delete");
  return airtableFetch(tableUrl(tableId, `/${recordId}`), { method: "DELETE" });
}

// ================= CLEAR ASSETS BEFORE DELETE =================
// Clears all linked asset fields on a row by setting them to [].
// This triggers Airtable automations to remove orphaned operator view records
// BEFORE the row itself is deleted.
async function clearRowAssetsInTable(tableId, recordId) {
  if (!recordId) throw new Error("Missing recordId for clearAssets");
  const fields = {};

  // Build an explicit "clear all" payload with null values for each asset field.
  // The undefined guards in applyLinkedAssets require explicit keys to be present,
  // so we pass null (not undefined) to signal "clear this field".
  const clearPayload = {
    ledAllTypesId: null,
    bigGfxVideosId: null,
    cornerGfxVideosId: null,
    superFullId: null,
    super169Id: null,
    superSideId: null,
    mcgInterchangeId: null,
    marvelInterchangeId: null,
    marvel360Id: null,
    mcg360Id: null,
    mcgTiledLedId: null,
    marvelTiledLedId: null,
    bigTvcId: null,
    cornerTvcId: null,
    iptvGfxId: null,
  };
  applyLinkedAssets(tableId, fields, clearPayload);

  // If no linked asset fields apply to this table type, skip the PATCH
  if (Object.keys(fields).length === 0) return null;

  return airtableFetch(tableUrl(tableId), {
    method: "PATCH",
    body: JSON.stringify({ records: [{ id: recordId, fields }] }),
  });
}

// ================= OPERATOR VIEW CLEANUP ON DELETE =================
// When a MatchFlow row is deleted, find and delete matching rows from
// the CG Operator View and Ribbon Operator View tables to prevent stale/orphaned records.
// Matches by Name field. Silently skips if operator tables aren't configured.

// Resolve the operator base ID — operator tables may live in a different Airtable base.
// Falls back to BASE_ID if no operator base is configured.
function _getOperatorBaseId() {
  try {
    const DEFAULT_CLUB = process.env.REACT_APP_DEFAULT_CLUB || '';
    if (DEFAULT_CLUB) {
      const opBase = getOperatorBase(DEFAULT_CLUB);
      if (opBase?.baseId) return opBase.baseId;
    }
  } catch (e) { /* fallback */ }
  return BASE_ID;
}

async function findRecordsBySourceId(operatorTableId, sourceRecordId) {
  if (!operatorTableId || !sourceRecordId) return [];
  
  const operatorBaseId = _getOperatorBaseId();
  const formula = `{Source Record ID}='${sourceRecordId}'`;
  const params = new URLSearchParams();
  params.set('filterByFormula', formula);
  
  try {
    const url = `https://api.airtable.com/v0/${operatorBaseId}/${operatorTableId}?${params.toString()}`;
    const data = await airtableFetch(url);
    return (data.records || []).map(r => r.id);
  } catch (err) {
    console.warn('[airtable] findRecordsBySourceId failed for table', operatorTableId, err);
    return [];
  }
}

export async function deleteMatchingOperatorRows(sourceRecordId) {
  if (!sourceRecordId) return;
  
  const operatorBaseId = _getOperatorBaseId();
  const tablesToClean = [
    { id: CG_OPERATOR_TABLE_ID, label: 'CG Operator' },
    { id: RIBBON_OPERATOR_TABLE_ID, label: 'Ribbon Operator' },
  ].filter(t => t.id); // Skip unconfigured tables

  if (tablesToClean.length === 0) {
    console.log('[airtable] No operator tables configured — skipping cleanup');
    return;
  }

  for (const table of tablesToClean) {
    const matchingIds = await findRecordsBySourceId(table.id, sourceRecordId);
    if (matchingIds.length === 0) continue;

    console.log(`[airtable] Cleaning ${matchingIds.length} row(s) from ${table.label} for source record ${sourceRecordId}`);
    
    // Airtable DELETE supports up to 10 records per request
    const chunkSize = 10;
    for (let i = 0; i < matchingIds.length; i += chunkSize) {
      const chunk = matchingIds.slice(i, i + chunkSize);
      const params = chunk.map(id => `records[]=${id}`).join('&');
      try {
        await airtableFetch(
          `https://api.airtable.com/v0/${operatorBaseId}/${table.id}?${params}`,
          { method: 'DELETE' }
        );
      } catch (err) {
        console.error(`[airtable] Failed to delete from ${table.label}:`, err);
      }
    }
  }
}

async function updateCueOrderInTable(tableId, items) {
  if (!Array.isArray(items) || items.length === 0) return true;

  const records = items.map((it) => ({
    id: it.id,
    fields: { [FIELDS.cue]: asNumber(it.cue) },
  }));

  const chunkSize = 10;
  for (let i = 0; i < records.length; i += chunkSize) {
    const chunk = records.slice(i, i + chunkSize);
    await airtableFetch(tableUrl(tableId), {
      method: "PATCH",
      body: JSON.stringify({ records: chunk }),
    });
  }

  return true;
}

// ================= OUT OF GAME EXPORTS =================
export async function createOutOfGameRow(payload) {
  return createRowInTable(TABLE_ID, payload, { requireFixture: true });
}

export async function updateOutOfGameRow(recordId, payload) {
  return updateRowInTable(TABLE_ID, recordId, payload, { requireFixture: true });
}

export async function deleteOutOfGameRow(recordId) {
  return deleteRowInTable(TABLE_ID, recordId);
}

export async function clearOutOfGameRowAssets(recordId) {
  return clearRowAssetsInTable(TABLE_ID, recordId);
}

export async function updateCueOrder(items) {
  return updateCueOrderInTable(TABLE_ID, items);
}

// ================= IN GAME EXPORTS =================
export async function createInGameRow(payload) {
  assertInGameTable();
  return createRowInTable(IN_GAME_TABLE_ID, payload, { requireFixture: true });
}

export async function updateInGameRow(recordId, payload) {
  assertInGameTable();
  return updateRowInTable(IN_GAME_TABLE_ID, recordId, payload, { requireFixture: true });
}

export async function deleteInGameRow(recordId) {
  assertInGameTable();
  return deleteRowInTable(IN_GAME_TABLE_ID, recordId);
}

export async function clearInGameRowAssets(recordId) {
  assertInGameTable();
  return clearRowAssetsInTable(IN_GAME_TABLE_ID, recordId);
}

export async function updateCueOrderInGame(items) {
  assertInGameTable();
  return updateCueOrderInTable(IN_GAME_TABLE_ID, items);
}

// ================= IPTV EXPORTS =================
export async function createIptvRow(payload) {
  assertIptvTable();
  return createRowInTable(IPTV_TABLE_ID, payload, { requireFixture: true });
}

export async function updateIptvRow(recordId, payload) {
  assertIptvTable();
  return updateRowInTable(IPTV_TABLE_ID, recordId, payload, { requireFixture: true });
}

export async function deleteIptvRow(recordId) {
  assertIptvTable();
  return deleteRowInTable(IPTV_TABLE_ID, recordId);
}

export async function clearIptvRowAssets(recordId) {
  assertIptvTable();
  return clearRowAssetsInTable(IPTV_TABLE_ID, recordId);
}

export async function updateCueOrderIptv(items) {
  assertIptvTable();
  return updateCueOrderInTable(IPTV_TABLE_ID, items);
}

// ================= LED ENTITLEMENTS EXPORTS =================
export async function createLedRow(payload) {
  assertLedTable();
  const requireFixture = payload.fixture !== null && payload.fixture !== undefined;
  return createRowInTable(LED_TABLE_ID, payload, { requireFixture });
}

export async function updateLedRow(recordId, payload) {
  assertLedTable();
  return updateRowInTable(LED_TABLE_ID, recordId, payload, { requireFixture: false });
}

export async function deleteLedRow(recordId) {
  assertLedTable();
  return deleteRowInTable(LED_TABLE_ID, recordId);
}

export async function clearLedRowAssets(recordId) {
  assertLedTable();
  return clearRowAssetsInTable(LED_TABLE_ID, recordId);
}

export async function updateCueOrderLed(items) {
  assertLedTable();
  return updateCueOrderInTable(LED_TABLE_ID, items);
}

// ================= TEAM LIST EXPORTS =================
async function createTeamRow(payload) {
  assertTeamListTable();
  return createRowInTable(TEAMLIST_TABLE_ID, payload, { requireFixture: false });
}

async function updateTeamRow(recordId, payload) {
  assertTeamListTable();
  return updateRowInTable(TEAMLIST_TABLE_ID, recordId, payload, { requireFixture: false });
}

async function deleteTeamRow(recordId) {
  assertTeamListTable();
  return deleteRowInTable(TEAMLIST_TABLE_ID, recordId);
}

async function updateCueOrderTeam(items) {
  assertTeamListTable();
  return updateCueOrderInTable(TEAMLIST_TABLE_ID, items);
}

export async function createTeamListRow(payload) {
  return createTeamRow(payload);
}

export async function updateTeamListRow(recordId, payload) {
  return updateTeamRow(recordId, payload);
}

export async function deleteTeamListRow(recordId) {
  return deleteTeamRow(recordId);
}

export async function clearTeamListRowAssets(recordId) {
  assertTeamListTable();
  return clearRowAssetsInTable(TEAMLIST_TABLE_ID, recordId);
}

export async function updateCueOrderTeamList(items) {
  return updateCueOrderTeam(items);
}

// ================= ASSET ARCHIVE/RESTORE =================
// Archive or restore assets by setting the "Archived" checkbox field

/**
 * Archive a single asset
 * @param {string} assetId - Airtable record ID of the asset
 * @returns {Promise<Object>} Updated record
 */
export async function archiveAsset(assetId, lumen8Id) {
  if (!assetId) throw new Error("Asset ID required");
  console.log(`[archiveAsset] called assetId=${assetId} lumen8Id=${lumen8Id || "(none)"}`);

  // Try known field names for the Archived checkbox
  const archivedFieldNames = ["Archived", "Is Archived", "Hidden", "Is Hidden"];

  let clubResult = null;
  for (const fieldName of archivedFieldNames) {
    try {
      const result = await airtableFetch(tableUrl(ASSETS_TABLE_ID), {
        method: "PATCH",
        body: JSON.stringify({
          records: [{ id: assetId, fields: { [fieldName]: true } }],
        }),
      });
      clubResult = result?.records?.[0] || result;
      break;
    } catch (err) {
      if (err?.message?.includes("Unknown field name")) {
        continue;
      }
      throw err;
    }
  }

  if (!clubResult) {
    throw new Error("Could not find 'Archived' field in Assets table. Please add a checkbox field named 'Archived' to your Assets table in Airtable.");
  }

  // Self-lookup fallback: if caller didn't pass a lumen8Id, read it off the
  // club record we just PATCHed. The PATCH response includes the full fields
  // object, so no extra fetch is needed.
  let effectiveLumen8Id = lumen8Id;
  if (!effectiveLumen8Id && clubResult?.fields) {
    effectiveLumen8Id = pickLumenId(clubResult.fields);
    if (effectiveLumen8Id) {
      console.log(`[archiveAsset] resolved lumen8Id from club record: ${effectiveLumen8Id}`);
    }
  }

  // Sync to commercial Content Library (non-blocking — failure logs, doesn't throw)
  if (effectiveLumen8Id) {
    try {
      const { syncCommercialAssetArchiveByLumen8Id } = await import("./services/commercialAirtable");
      const sync = await syncCommercialAssetArchiveByLumen8Id(effectiveLumen8Id, true);
      if (!sync.found) console.warn(`[archiveAsset] No matching commercial asset for Lumen8 ID: ${effectiveLumen8Id}`);
      else if (!sync.updated) console.warn(`[archiveAsset] Commercial sync failed for Lumen8 ID: ${effectiveLumen8Id}`);
      else console.log(`[archiveAsset] ✅ Synced archive to commercial base for ${effectiveLumen8Id}`);
    } catch (syncErr) {
      console.warn(`[archiveAsset] Commercial sync error:`, syncErr.message);
    }
  } else {
    console.warn(`[archiveAsset] No Lumen8 ID resolvable for assetId=${assetId} — commercial sync SKIPPED. Check that the Lumen8 ID field on the club Assets table is populated for this record.`);
  }

  return clubResult;
}

/**
 * Restore (un-archive) a single asset
 * @param {string} assetId - Airtable record ID of the asset
 * @returns {Promise<Object>} Updated record
 */
export async function restoreAsset(assetId, lumen8Id) {
  if (!assetId) throw new Error("Asset ID required");
  
  const archivedFieldNames = ["Archived", "Is Archived", "Hidden", "Is Hidden"];
  
  let clubResult = null;
  for (const fieldName of archivedFieldNames) {
    try {
      const result = await airtableFetch(tableUrl(ASSETS_TABLE_ID), {
        method: "PATCH",
        body: JSON.stringify({
          records: [{ id: assetId, fields: { [fieldName]: false } }],
        }),
      });
      clubResult = result?.records?.[0] || result;
      break;
    } catch (err) {
      if (err?.message?.includes("Unknown field name")) {
        continue;
      }
      throw err;
    }
  }
  
  if (!clubResult) {
    throw new Error("Could not find 'Archived' field in Assets table.");
  }

  // Sync to commercial base (non-blocking)
  if (lumen8Id) {
    try {
      const { syncCommercialAssetArchiveByLumen8Id } = await import("./services/commercialAirtable");
      const sync = await syncCommercialAssetArchiveByLumen8Id(lumen8Id, false);
      if (!sync.found) console.warn(`[restoreAsset] No matching commercial asset for Lumen8 ID: ${lumen8Id}`);
      else if (!sync.updated) console.warn(`[restoreAsset] Commercial sync failed for Lumen8 ID: ${lumen8Id}`);
      else console.log(`[restoreAsset] Synced restore to commercial base for ${lumen8Id}`);
    } catch (syncErr) {
      console.warn(`[restoreAsset] Commercial sync error:`, syncErr.message);
    }
  }

  return clubResult;
}

/**
 * Archive multiple assets at once
 * @param {string[]} assetIds - Array of Airtable record IDs
 * @returns {Promise<Object>} Updated records
 */
export async function archiveAssets(assetIds, lumen8Ids = []) {
  if (!assetIds?.length) return { records: [] };
  
  const archivedFieldNames = ["Archived", "Is Archived", "Hidden", "Is Hidden"];
  
  let clubResult = null;
  for (const fieldName of archivedFieldNames) {
    try {
      const result = await airtableFetch(tableUrl(ASSETS_TABLE_ID), {
        method: "PATCH",
        body: JSON.stringify({
          records: assetIds.map(id => ({ id, fields: { [fieldName]: true } })),
        }),
      });
      clubResult = result;
      break;
    } catch (err) {
      if (err?.message?.includes("Unknown field name")) {
        continue;
      }
      throw err;
    }
  }
  
  if (!clubResult) throw new Error("Could not find 'Archived' field in Assets table.");

  // Sync to commercial base for each Lumen8 ID provided
  if (lumen8Ids?.length) {
    try {
      const { syncCommercialAssetArchiveByLumen8Id } = await import("./services/commercialAirtable");
      for (const lid of lumen8Ids.filter(Boolean)) {
        const r = await syncCommercialAssetArchiveByLumen8Id(lid, true);
        if (!r.found) console.warn(`[archiveAssets] No commercial match for ${lid}`);
      }
    } catch (err) { console.warn("[archiveAssets] Sync error:", err.message); }
  }
  return clubResult;
}

/**
 * Restore multiple assets at once
 * @param {string[]} assetIds - Array of Airtable record IDs
 * @param {string[]} lumen8Ids - Optional matching Lumen8 IDs for commercial sync
 * @returns {Promise<Object>} Updated records
 */
export async function restoreAssets(assetIds, lumen8Ids = []) {
  if (!assetIds?.length) return { records: [] };
  
  const archivedFieldNames = ["Archived", "Is Archived", "Hidden", "Is Hidden"];
  
  let clubResult = null;
  for (const fieldName of archivedFieldNames) {
    try {
      const result = await airtableFetch(tableUrl(ASSETS_TABLE_ID), {
        method: "PATCH",
        body: JSON.stringify({
          records: assetIds.map(id => ({ id, fields: { [fieldName]: false } })),
        }),
      });
      clubResult = result;
      break;
    } catch (err) {
      if (err?.message?.includes("Unknown field name")) {
        continue;
      }
      throw err;
    }
  }
  
  if (!clubResult) throw new Error("Could not find 'Archived' field in Assets table.");

  if (lumen8Ids?.length) {
    try {
      const { syncCommercialAssetArchiveByLumen8Id } = await import("./services/commercialAirtable");
      for (const lid of lumen8Ids.filter(Boolean)) {
        const r = await syncCommercialAssetArchiveByLumen8Id(lid, false);
        if (!r.found) console.warn(`[restoreAssets] No commercial match for ${lid}`);
      }
    } catch (err) { console.warn("[restoreAssets] Sync error:", err.message); }
  }
  return clubResult;
}

/**
 * Update Round Allocations on an asset record
 * @param {string} assetId - Airtable record ID of the asset
 * @param {Array} allocations - Array of {round, numberOfRotations} objects
 * @returns {Promise<Object>} Updated record
 */
export async function updateAssetRoundAllocations(assetId, allocations) {
  if (!assetId) throw new Error("Asset ID required");
  
  const jsonStr = JSON.stringify(allocations);
  const fieldNames = ["Round Allocations", "Round Allocation", "Rounds Allocation"];
  
  for (const fieldName of fieldNames) {
    try {
      const result = await airtableFetch(tableUrl(ASSETS_TABLE_ID), {
        method: "PATCH",
        body: JSON.stringify({
          records: [{ id: assetId, fields: { [fieldName]: jsonStr } }],
        }),
      });
      return result?.records?.[0] || result;
    } catch (err) {
      if (err?.message?.includes("Unknown field name")) {
        continue;
      }
      throw err;
    }
  }
  
  throw new Error("Could not find 'Round Allocations' field in Assets table.");
}

export { updateCueOrderTeam };
